import * as THREE from 'three';
import type { PlayerColor } from '../shared/protocol';
import { createPlayerModel } from './game/PlayerModel';

const disposeModel = (model: THREE.Object3D): void => {
  model.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    node.geometry.dispose();
    const materials = Array.isArray(node.material) ? node.material : [node.material];
    materials.forEach((material) => material.dispose());
  });
};

export class SuitPreview {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(30, 1, 0.1, 20);
  private readonly resizeObserver: ResizeObserver;
  private model: THREE.Group;
  private active = false;
  private animationFrame = 0;
  private color: PlayerColor;

  constructor(private readonly canvas: HTMLCanvasElement, initialColor: PlayerColor) {
    this.color = initialColor;
    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'low-power' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;

    this.camera.position.set(2.7, 1.65, -4.5);
    this.camera.lookAt(0, 1.05, 0);
    this.scene.add(new THREE.HemisphereLight(0xd9eeee, 0x1b1210, 2.4));
    const keyLight = new THREE.DirectionalLight(0xffd36a, 4.6);
    keyLight.position.set(-2.5, 4, -3);
    const rimLight = new THREE.DirectionalLight(0x58c9d4, 3.4);
    rimLight.position.set(3, 2, 2);
    this.scene.add(keyLight, rimLight);

    const platformMaterial = new THREE.MeshStandardMaterial({ color: 0x20282a, metalness: 0.72, roughness: 0.34, flatShading: true });
    const platform = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.08, 0.18, 12), platformMaterial);
    platform.position.y = -0.02;
    const signalRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.78, 0.035, 5, 20),
      new THREE.MeshStandardMaterial({ color: 0x58c9d4, emissive: 0x2e9da8, emissiveIntensity: 1.1, roughness: 0.25 }),
    );
    signalRing.position.y = 0.08;
    signalRing.rotation.x = Math.PI / 2;
    this.scene.add(platform, signalRing);

    this.model = createPlayerModel(initialColor, false);
    this.model.scale.setScalar(1.08);
    this.scene.add(this.model);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
    this.render(0);
  }

  setColor(color: PlayerColor): void {
    if (color === this.color) return;
    this.color = color;
    this.scene.remove(this.model);
    disposeModel(this.model);
    this.model = createPlayerModel(color, false);
    this.model.scale.setScalar(1.08);
    this.scene.add(this.model);
    if (!this.active) this.render(0);
  }

  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    window.cancelAnimationFrame(this.animationFrame);
    if (active) {
      this.resize();
      this.animationFrame = window.requestAnimationFrame(this.render);
    }
  }

  private readonly resize = (): void => {
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    if (!this.active) this.renderer.render(this.scene, this.camera);
  };

  private readonly render = (time: number): void => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.model.rotation.y = reduceMotion ? -0.18 : -0.18 + Math.sin(time * 0.00048) * 0.52;
    this.renderer.render(this.scene, this.camera);
    if (this.active) this.animationFrame = window.requestAnimationFrame(this.render);
  };
}

